export type BucketKey =
  | 'What'
  | 'Why'
  | 'Sell'
  | 'Differentiate'
  | 'Configure'
  | 'Validate'
  | 'Troubleshoot';

export const bucketOrder: BucketKey[] = [
  'What',
  'Why',
  'Sell',
  'Differentiate',
  'Configure',
  'Validate',
  'Troubleshoot',
];

type RawLink = {
  label?: string;
  title?: string;
  name?: string;
  url?: string;
  link?: string;
  href?: string;
};

type RawResource = {
  id?: string | number;
  type?: string;
  title?: string;
  name?: string;
  description?: string;
  content?: string;
  markdown?: string;
  url?: string;
  link?: string;
  href?: string;
  last_updated?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
};

type RawFeatureRef = {
  id?: string | number;
};

type NamedValue = {
  id?: string | number;
  name?: string;
  title?: string;
};

type RawBase = {
  id?: string | number;
  name?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  summary?: string;
  why_its_important?: string;
  how_to_sell?: string;
  differentiation?: string;
  configure?: string;
  validate?: string;
  troubleshoot?: string;
  architecture?: string[] | string | NamedValue[];
  architectures?: string[] | string | NamedValue[];
  architecture_name?: string[] | string | NamedValue[];
  tags?: string[] | string;
  resource?: RawResource[];
  resources?: RawResource[];
  links?: RawLink[];
  product_site_url?: string;
  productSiteUrl?: string;
  sales_url?: string;
  sales_connect_url?: string;
  salesConnectUrl?: string;
  salesconnect_url?: string;
  offer_type?: string;
  offerType?: string;
};

type RawFeature = RawBase;

type RawGroup = RawBase & {
  product_feature?: RawFeatureRef[];
};

type RawVersion = {
  id?: string | number;
  version?: string;
  name?: string;
  title?: string;
  description?: string;
  released?: string;
  release_date?: string;
  lastUpdate?: string;
  last_update?: string;
  endOfLife?: string;
  end_of_life?: string;
  release_notes_url?: string;
  releaseNotesUrl?: string;
  install_guide_url?: string;
  installGuideUrl?: string;
  admin_guide_url?: string;
  adminGuideUrl?: string;
  datasheet_url?: string;
  dataSheetUrl?: string;
  api_documentation?: string;
  api_documentation_url?: string;
  api_docs_url?: string;
  apiDocsUrl?: string;
  swagger_url?: string;
  developer_guide_url?: string;
  developerGuideUrl?: string;
  configuration_guide?: string;
  configuration_guide_url?: string;
  configuration_guides_url?: string;
  config_guide_url?: string;
  configurationGuideUrl?: string;
  configurationGuidesUrl?: string;
  user_guide_url?: string;
  userGuideUrl?: string;
  getting_started_url?: string;
  gettingStartedUrl?: string;
  command_reference_url?: string;
  commandReferenceUrl?: string;
  links?: RawLink[];
  resources?: RawLink[];
};

type RawProduct = RawBase & {
  vendor?: { is_cisco?: boolean; name?: string } | string;
  feature?: RawFeature[];
  use_case?: RawGroup[];
  component?: RawGroup[];
  version?: RawVersion[];
};

export type DashboardLink = {
  label: string;
  url: string;
};

export type DashboardCapability = {
  bucket: BucketKey;
  title: string;
  summary?: string;
  markdown: string;
  links?: DashboardLink[];
  sourceType: 'product' | 'use_case' | 'component' | 'feature' | 'resource';
  sourceName: string;
  sourceId?: string;
  last_updated?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
};

export type DashboardEntity = {
  id: string;
  name: string;
  subtitle?: string;
  summary?: string;
  description?: string;
  capabilities: Partial<Record<BucketKey, DashboardCapability>>;
};

export type DashboardFeature = DashboardEntity & {
  type: 'feature';
  raw: RawFeature;
};

export type DashboardGroup = DashboardEntity & {
  type: 'use_case' | 'component';
  raw: RawGroup;
  features: DashboardFeature[];
  bucketTargets: Partial<Record<BucketKey, DashboardFeature | null>>;
};

export type DashboardVersion = {
  id: string;
  name: string;
  description?: string;
  released?: string;
  lastUpdate?: string;
  endOfLife?: string;
  links: DashboardLink[];
};

export type DashboardProduct = DashboardEntity & {
  type: 'product';
  raw: RawProduct;
  vendor?: string;
  offerType?: string;
  architectures: string[];
  features: DashboardFeature[];
  useCaseGroups: DashboardGroup[];
  componentGroups: DashboardGroup[];
  versions: DashboardVersion[];
  bucketTargets: Partial<Record<BucketKey, DashboardFeature | null>>;
  externalLinks: DashboardLink[];
};

const bucketTypeMap: Record<string, BucketKey> = {
  what: 'What',
  why: 'Why',
  sell: 'Sell',
  differentiate: 'Differentiate',
  configure: 'Configure',
  validate: 'Validate',
  troubleshoot: 'Troubleshoot',
};

const fieldMap: Record<BucketKey, keyof RawBase> = {
  What: 'description',
  Why: 'why_its_important',
  Sell: 'how_to_sell',
  Differentiate: 'differentiation',
  Configure: 'configure',
  Validate: 'validate',
  Troubleshoot: 'troubleshoot',
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toId(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toName(input: { name?: string; title?: string }, fallback: string): string {
  return asString(input.name) || asString(input.title) || fallback;
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return asString(item);
        if (item && typeof item === 'object') {
          return asString((item as NamedValue).name) || asString((item as NamedValue).title);
        }
        return '';
      })
      .filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function getOfferTypeLabel(value: unknown): string {
  const normalized = asString(value).toLowerCase();

  switch (normalized) {
    case 'appliance_product':
      return 'Appliance';
    case 'hybrid_product':
      return 'Hybrid';
    case 'saas_product':
      return 'SaaS';
    case 'services':
      return 'Services';
    default:
      return asString(value);
  }
}

function getUrl(value: any): string {
  return asString(value?.url || value?.link || value?.href);
}

function getLabel(value: any): string {
  return asString(value?.label || value?.title || value?.name);
}

function normalizeLinks(links?: RawLink[]): DashboardLink[] {
  if (!Array.isArray(links)) return [];
  return links
    .map((link) => {
      const url = getUrl(link);
      if (!url) return null;
      return {
        label: getLabel(link) || url,
        url,
      };
    })
    .filter((item): item is DashboardLink => !!item);
}

function dedupeLinks(links: DashboardLink[]): DashboardLink[] {
  return links.filter(
    (link, index, arr) =>
      arr.findIndex((item) => item.url === link.url && item.label === link.label) === index
  );
}

function normalizeBucketType(type?: string): BucketKey | null {
  if (!type) return null;
  return bucketTypeMap[type.trim().toLowerCase()] || null;
}

function getAllResources(entity: RawBase): RawResource[] {
  const direct = Array.isArray(entity.resource) ? entity.resource : [];
  const plural = Array.isArray(entity.resources) ? entity.resources : [];
  return [...direct, ...plural];
}

function getCapabilityFromField(
  entity: RawBase,
  bucket: BucketKey,
  sourceType: DashboardCapability['sourceType'],
  sourceName: string
): DashboardCapability | null {
  const field = fieldMap[bucket];
  const markdown = asString(entity[field]);

  if (!markdown) return null;

  return {
    bucket,
    title: `${sourceName} — ${bucket}`,
    summary: asString(entity.summary),
    markdown,
    links: normalizeLinks(entity.links),
    sourceType,
    sourceName,
    sourceId: toId(entity.id),
  };
}

function getCapabilityFromResource(
  entity: RawBase,
  bucket: BucketKey,
  sourceName: string
): DashboardCapability | null {
  const resources = getAllResources(entity);
  const matching = resources.filter((item) => normalizeBucketType(item.type) === bucket);

  if (!matching.length) return null;

  const primaryResource = matching[0];

  const markdown = matching
    .map((item) => {
      const parts = [
        asString(item.title || item.name),
        asString(item.markdown),
        asString(item.description),
        asString(item.content),
      ].filter(Boolean);
      return parts.join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n---\n\n')
    .trim();

  const links = dedupeLinks(
    matching
      .map((item) => {
        const url = getUrl(item);
        if (!url) return null;
        return {
          label: getLabel(item) || url,
          url,
        };
      })
      .filter((item): item is DashboardLink => !!item)
  );

  if (!markdown && !links.length) return null;

  return {
    bucket,
    title: `${sourceName} — ${bucket}`,
    summary: asString(entity.summary),
    markdown: markdown || '',
    links,
    sourceType: 'resource',
    sourceName,
    sourceId: toId(primaryResource?.id),
    last_updated: asString(primaryResource?.last_updated),
    updated_by: asString(primaryResource?.updated_by),
    created_at: asString(primaryResource?.created_at),
    updated_at: asString(primaryResource?.updated_at),
  };
}

function buildFeatureCapabilities(
  feature: RawFeature,
  name: string
): Partial<Record<BucketKey, DashboardCapability>> {
  const capabilities: Partial<Record<BucketKey, DashboardCapability>> = {};

  bucketOrder.forEach((bucket) => {
    if (bucket === 'What') {
      const resourceCapability = getCapabilityFromResource(feature, bucket, name);
      if (resourceCapability) {
        capabilities[bucket] = resourceCapability;
        return;
      }
    }

    const fieldCapability = getCapabilityFromField(feature, bucket, 'feature', name);
    if (fieldCapability) {
      capabilities[bucket] = fieldCapability;
      return;
    }

    const resourceCapability = getCapabilityFromResource(feature, bucket, name);
    if (resourceCapability) {
      capabilities[bucket] = resourceCapability;
    }
  });

  return capabilities;
}

function deriveBucketTargets(
  features: DashboardFeature[]
): Partial<Record<BucketKey, DashboardFeature | null>> {
  const targets: Partial<Record<BucketKey, DashboardFeature | null>> = {};

  bucketOrder.forEach((bucket) => {
    targets[bucket] = features.find((feature) => !!feature.capabilities?.[bucket]) || null;
  });

  return targets;
}

function buildGroupCapabilities(
  group: RawGroup,
  type: 'use_case' | 'component',
  name: string,
  linkedFeatures: DashboardFeature[]
): Partial<Record<BucketKey, DashboardCapability>> {
  const capabilities: Partial<Record<BucketKey, DashboardCapability>> = {};

  bucketOrder.forEach((bucket) => {
    if (bucket === 'What') {
      const resourceCapability = getCapabilityFromResource(group, bucket, name);
      if (resourceCapability) {
        capabilities[bucket] = resourceCapability;
        return;
      }
    }

    const fieldCapability = getCapabilityFromField(group, bucket, type, name);
    if (fieldCapability) {
      capabilities[bucket] = fieldCapability;
      return;
    }

    const resourceCapability = getCapabilityFromResource(group, bucket, name);
    if (resourceCapability) {
      capabilities[bucket] = resourceCapability;
      return;
    }

    const fallback = linkedFeatures.find((feature) => !!feature.capabilities?.[bucket]);
    if (fallback?.capabilities?.[bucket]) {
      capabilities[bucket] = fallback.capabilities[bucket];
    }
  });

  return capabilities;
}

function buildProductCapabilities(
  product: RawProduct,
  name: string,
  features: DashboardFeature[]
): Partial<Record<BucketKey, DashboardCapability>> {
  const capabilities: Partial<Record<BucketKey, DashboardCapability>> = {};

  bucketOrder.forEach((bucket) => {
    if (bucket === 'What') {
      const resourceCapability = getCapabilityFromResource(product, bucket, name);
      if (resourceCapability) {
        capabilities[bucket] = resourceCapability;
        return;
      }
    }

    const fieldCapability = getCapabilityFromField(product, bucket, 'product', name);
    if (fieldCapability) {
      capabilities[bucket] = fieldCapability;
      return;
    }

    const resourceCapability = getCapabilityFromResource(product, bucket, name);
    if (resourceCapability) {
      capabilities[bucket] = resourceCapability;
      return;
    }

    const fallback = features.find((feature) => !!feature.capabilities?.[bucket]);
    if (fallback?.capabilities?.[bucket]) {
      capabilities[bucket] = fallback.capabilities[bucket];
    }
  });

  return capabilities;
}

function buildDashboardFeature(feature: RawFeature): DashboardFeature {
  const name = toName(feature, 'Feature');
  return {
    id: toId(feature.id),
    type: 'feature',
    raw: feature,
    name,
    subtitle: asString(feature.subtitle),
    summary: asString(feature.summary),
    description: asString(feature.description),
    capabilities: buildFeatureCapabilities(feature, name),
  };
}

function buildDashboardVersion(version: RawVersion): DashboardVersion {
  const extraLinks: DashboardLink[] = [
    version.release_notes_url ? { label: 'Release Notes', url: asString(version.release_notes_url) } : null,
    version.releaseNotesUrl ? { label: 'Release Notes', url: asString(version.releaseNotesUrl) } : null,
    version.install_guide_url ? { label: 'Install Guide', url: asString(version.install_guide_url) } : null,
    version.installGuideUrl ? { label: 'Install Guide', url: asString(version.installGuideUrl) } : null,
    version.admin_guide_url ? { label: 'Admin Guide', url: asString(version.admin_guide_url) } : null,
    version.adminGuideUrl ? { label: 'Admin Guide', url: asString(version.adminGuideUrl) } : null,
    version.datasheet_url ? { label: 'Datasheet', url: asString(version.datasheet_url) } : null,
    version.dataSheetUrl ? { label: 'Datasheet', url: asString(version.dataSheetUrl) } : null,

    version.api_documentation ? { label: 'API Documentation', url: asString(version.api_documentation) } : null,
    version.api_documentation_url ? { label: 'API Documentation', url: asString(version.api_documentation_url) } : null,
    version.api_docs_url ? { label: 'API Documentation', url: asString(version.api_docs_url) } : null,
    version.apiDocsUrl ? { label: 'API Documentation', url: asString(version.apiDocsUrl) } : null,

    version.swagger_url ? { label: 'Swagger', url: asString(version.swagger_url) } : null,
    version.developer_guide_url ? { label: 'Developer Guide', url: asString(version.developer_guide_url) } : null,
    version.developerGuideUrl ? { label: 'Developer Guide', url: asString(version.developerGuideUrl) } : null,

    version.configuration_guide ? { label: 'Configuration Guide', url: asString(version.configuration_guide) } : null,
    version.configuration_guide_url ? { label: 'Configuration Guide', url: asString(version.configuration_guide_url) } : null,
    version.configuration_guides_url ? { label: 'Configuration Guide', url: asString(version.configuration_guides_url) } : null,
    version.config_guide_url ? { label: 'Configuration Guide', url: asString(version.config_guide_url) } : null,
    version.configurationGuideUrl ? { label: 'Configuration Guide', url: asString(version.configurationGuideUrl) } : null,
    version.configurationGuidesUrl ? { label: 'Configuration Guide', url: asString(version.configurationGuidesUrl) } : null,

    version.user_guide_url ? { label: 'User Guide', url: asString(version.user_guide_url) } : null,
    version.userGuideUrl ? { label: 'User Guide', url: asString(version.userGuideUrl) } : null,
    version.getting_started_url ? { label: 'Getting Started', url: asString(version.getting_started_url) } : null,
    version.gettingStartedUrl ? { label: 'Getting Started', url: asString(version.gettingStartedUrl) } : null,
    version.command_reference_url ? { label: 'Command Reference', url: asString(version.command_reference_url) } : null,
    version.commandReferenceUrl ? { label: 'Command Reference', url: asString(version.commandReferenceUrl) } : null,
  ].filter((item): item is DashboardLink => !!item && !!item.url);

  const normalizedVersionLinks = normalizeLinks(version.links);
  const normalizedResourceLinks = normalizeLinks(version.resources);

  return {
    id: toId(version.id) || asString(version.version) || asString(version.name) || 'version',
    name: asString(version.version) || asString(version.name) || asString(version.title) || 'Version',
    description: asString(version.description),
    released: asString(version.released) || asString(version.release_date),
    lastUpdate: asString(version.lastUpdate) || asString(version.last_update),
    endOfLife: asString(version.endOfLife) || asString(version.end_of_life),
    links: dedupeLinks([...normalizedVersionLinks, ...normalizedResourceLinks, ...extraLinks]),
  };
}

function getArchitectures(product: RawProduct): string[] {
  return Array.from(
    new Set(
      [
        ...normalizeStringArray(product.architectures),
        ...normalizeStringArray(product.architecture),
        ...normalizeStringArray(product.architecture_name),
      ].filter(Boolean)
    )
  );
}

function getProductExternalLinks(product: RawProduct): DashboardLink[] {
  const links: DashboardLink[] = [];

  const productSite =
    asString(product.product_site_url) ||
    asString(product.productSiteUrl);

  const salesPage = asString(product.sales_url);

  const salesConnect =
    asString(product.sales_connect_url) ||
    asString(product.salesConnectUrl) ||
    asString(product.salesconnect_url);

  if (productSite) {
    links.push({
      label: 'Product Site',
      url: productSite,
    });
  }

  if (salesPage) {
    links.push({
      label: 'Sales Page',
      url: salesPage,
    });
  }

  if (salesConnect) {
    links.push({
      label: 'SalesConnect',
      url: salesConnect,
    });
  }

  return dedupeLinks(links);
}

export function getProductDashboardModel(product: any): DashboardProduct {
  const rawProduct = (product || {}) as RawProduct;
  const featuresRaw = Array.isArray(rawProduct.feature) ? rawProduct.feature : [];
  const useCasesRaw = Array.isArray(rawProduct.use_case) ? rawProduct.use_case : [];
  const componentsRaw = Array.isArray(rawProduct.component) ? rawProduct.component : [];
  const versionsRaw = Array.isArray(rawProduct.version) ? rawProduct.version : [];

  const features = featuresRaw.map(buildDashboardFeature);
  const featureMap = new Map(features.map((feature) => [String(feature.id), feature]));

  const useCaseGroups: DashboardGroup[] = useCasesRaw
    .map((group) => {
      const linkedFeatures = (group.product_feature || [])
        .map((ref) => featureMap.get(String(ref?.id)))
        .filter((item): item is DashboardFeature => !!item);

      if (!linkedFeatures.length) return null;

      const name = toName(group, 'Use Case');

      return {
        id: toId(group.id),
        type: 'use_case' as const,
        raw: group,
        name,
        subtitle: asString(group.subtitle),
        summary: asString(group.summary),
        description: asString(group.description),
        features: linkedFeatures,
        bucketTargets: deriveBucketTargets(linkedFeatures),
        capabilities: buildGroupCapabilities(group, 'use_case', name, linkedFeatures),
      };
    })
    .filter((item) => item !== null);

  const componentGroups: DashboardGroup[] = componentsRaw
    .map((group) => {
      const linkedFeatures = (group.product_feature || [])
        .map((ref) => featureMap.get(String(ref?.id)))
        .filter((item): item is DashboardFeature => !!item);

      if (!linkedFeatures.length) return null;

      const name = toName(group, 'Component');

      return {
        id: toId(group.id),
        type: 'component' as const,
        raw: group,
        name,
        subtitle: asString(group.subtitle),
        summary: asString(group.summary),
        description: asString(group.description),
        features: linkedFeatures,
        bucketTargets: deriveBucketTargets(linkedFeatures),
        capabilities: buildGroupCapabilities(group, 'component', name, linkedFeatures),
      };
    })
    .filter((item) => item !== null);

  const name = toName(rawProduct, 'Product');

  return {
    id: toId(rawProduct.id),
    type: 'product',
    raw: rawProduct,
    name,
    subtitle: asString(rawProduct.subtitle),
    summary: asString(rawProduct.summary),
    description: asString(rawProduct.description),
    vendor:
      typeof rawProduct.vendor === 'string'
        ? rawProduct.vendor
        : asString(rawProduct.vendor?.name),
    offerType: getOfferTypeLabel(rawProduct.offer_type || rawProduct.offerType),
    architectures: getArchitectures(rawProduct),
    features,
    useCaseGroups,
    componentGroups,
    versions: versionsRaw.map(buildDashboardVersion),
    bucketTargets: deriveBucketTargets(features),
    capabilities: buildProductCapabilities(rawProduct, name, features),
    externalLinks: getProductExternalLinks(rawProduct),
  };
}

export function getProductCapabilities(
  entity: DashboardEntity | null | undefined
): Partial<Record<BucketKey, DashboardCapability>> {
  return entity?.capabilities || {};
}